import _ from 'underscore';
import React, { useMemo, useContext, useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { OverviewCard } from '@bingads-webui-campaign-react/overview-card';
import { CardSize } from '@bingads-webui-campaign-react/overview-card-const';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { WaterfallFilterStorage, WaterfallFilterType, waterfallDateFilterProcessor } from '@bingads-webui-campaign/filter-storage';
import { getFilteredCampaignTypeFromWaterfallFilter } from '@bingads-webui-campaign-react/shell-ui-common';
import constants from '@bingads-webui-static-legacy/instrumentation/constants';
import instrumentation from '@bingads-webui-static-legacy/instrumentation/index';
import { PreferenceContext } from '@bingads-webui-react/preference-context';
import { CardSelector } from '@bingads-webui-campaign-react/card-selector';
import { PageReady } from '@bingads-webui/page-ready';
import { DisclaimerBanner } from '@bingads-webui-campaign-react/help';
import {
  getPreferencePatcher,
  getCardPreferenceKey,
  getNavigationLevel,
  useSelectedCardsWithPreference,
  getFluentCardsConfigs,
  getCardPreferenceWithDefault,
  getCardPreferenceOptionsForDfm,
  getViewPortCardsCount,
} from '@bingads-webui-campaign/fluent-page-data';
import { getCardViewConstructor } from '@bingads-webui-campaign/fluent-page-view-constructor';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import { CampaignStatusSummaryCards } from '@bingads-webui-campaign-react/campaign-status-summary-cards';
import { cardDiagnosticId, cardPerformanceId } from '@bingads-webui-campaign/fluent-page-consts';

import { legacyDeps } from './legacy-deps/index';
import { useOverviewPhase2WindowSizeChange } from './use-overview-window-size-change';
import { getChildPerfMarkers } from './get-child-perf-markers';
import { useStableObject } from './helper/use-stable-object';
import { useWelcomeTips } from './helper/use-welcome-tips';
import { useHasTooManyAccounts } from './too-many-accounts/use-has-too-many-accounts';
import { TooMuchAccountsError } from './too-many-accounts/too-many-accounts';
import { useOverviewPageStyles } from './style/overview-page-phase-2-style';

const campaignStateCard = 'CampaignStatusSummaryCards-CampaignState';
const budgetCard = 'CampaignStatusSummaryCards-Budget';
const bidStrategyCard = 'CampaignStatusSummaryCards-BidStrategy';
const SummaryCardsConfigs = [{ typeId: campaignStateCard }, { typeId: budgetCard }, { typeId: bidStrategyCard }];

const getCardSizeClass = (size, responsiveSize, styles) => {
  if (responsiveSize === 'xxl') {
    if (size === CardSize.Small) {
      return styles.cardWidth33;
    }
    return styles.cardWidth66;
  } else if (responsiveSize === 'xl') {
    if (size === CardSize.Small) {
      return styles.cardWidth50;
    }
    return styles.cardWidth100;
  }
  return styles.cardWidth100;
};

export const OverviewPagePhase2 = (props) => {
  const styles = useOverviewPageStyles();
  const { scope } = useContext(ScopeContext);
  const {
    serverContext,
    permissions,
    preferenceService,
    i18n,
    currentCampaign,
    perfMarker,
    isSDK,
  } = props;
  const responsiveSize = useOverviewPhase2WindowSizeChange();
  const preference = useContext(PreferenceContext);
  const instrumentationContextValue = useContext(InstrumentationContext);
  const { activity: dfmActivity } = useMemo(() => instrumentationContextValue.createScenarioAndActivity({
    scenarioName: 'Fluent2OverviewPage',
    activityName: 'DataFetchManager',
  }), [instrumentationContextValue]);

  const { activity: overviewPageActivity } = useMemo(() => instrumentationContextValue.createScenarioAndActivity({
    scenarioName: 'Fluent2OverviewPage',
    activityName: 'OverviewPage',
  }), [instrumentationContextValue]);

  // We use this key to force the cards to reset their internal preference state when certain props change
  const [refreshKey, setRefreshKey] = useState(0);

  const waterfallFilterStorage = useMemo(() => new WaterfallFilterStorage({ preferenceService }), [preferenceService]);

  // Cannot use useMemo here as it will get stale data
  const dateRange = waterfallDateFilterProcessor(
    waterfallFilterStorage.getFilters([WaterfallFilterType.DateFilter]),
    serverContext
  );
  const filter = waterfallFilterStorage.getFilters([
    WaterfallFilterType.CampaignType,
    WaterfallFilterType.CampaignStatus,
    WaterfallFilterType.AdGroupStatus,
  ]);
  const stableDateRange = useStableObject(dateRange);
  const stableFilter = useStableObject(filter);

  const filterCampaignType = getFilteredCampaignTypeFromWaterfallFilter(filter);

  const navigationLevel = useMemo(() => getNavigationLevel(scope.values), [scope.values]);

  const campaignType = useMemo(() => {
    let result = filterCampaignType;
    if (currentCampaign) {
      result = _.chain(currentCampaign).result('CampaignType').value();
    }
    return result;
  }, [currentCampaign, filterCampaignType]);

  const isLinkedInCampaignOverview = currentCampaign && campaignType === 'LinkedIn';
  const isPMaxCampaignOverview = currentCampaign && campaignType === 'PerformanceMax';
  const isShowCampaignStatusSummaryCards = isLinkedInCampaignOverview || isPMaxCampaignOverview;
  const isMobileAppCampaign = useMemo(() => {
    const { CampaignSettings, CampaignType } = currentCampaign || {};
    const isAppCampaign = CampaignType && CampaignType === 'App';
    const getCampaignSetting = CampaignSettings && CampaignSettings.find(setting => setting['@odata.type'] === '#Model.AppSetting');

    return isAppCampaign ? getCampaignSetting?.AppStore === 'AppleAppStore' || getCampaignSetting?.AppStore === 'GoogleAppStore' : false;
  }, [currentCampaign]);

  const welcomeTipsConfig = useWelcomeTips({
    preference, navigationLevel, serverContext, instrumentation, constants,
  });
  const isLoadingWelcomeCardConfig = !welcomeTipsConfig;
  const showWelcomeTips = welcomeTipsConfig && welcomeTipsConfig.showWelcomeTips;

  const cardsConfigs = useMemo(() => getFluentCardsConfigs({
    permissions,
    showWelcomeTips,
    isMobileAppCampaign,
    campaignType,
  }), [permissions, showWelcomeTips, isMobileAppCampaign, campaignType]);

  const {
    selectedCardsConfigs,
    cardSelectionCards,
    onCardSelectionSaved,
  } = useSelectedCardsWithPreference({
    preference,
    cardsConfigs,
    navigationLevel,
    campaignType: navigationLevel !== 'customer' && campaignType,
    serverContext,
    isLoadingWelcomeCardConfig,
    isSDK,
  });

  useEffect(() => {
    overviewPageActivity.trace(`Overview Page Info - navigationLevel: ${navigationLevel} - campaignType: ${campaignType} - isSDK: ${isSDK} - showWelcomeTips: ${showWelcomeTips} -`);
  }, [navigationLevel, campaignType, isSDK, showWelcomeTips, overviewPageActivity]);

  const getCardsConfig = useCallback(() => selectedCardsConfigs.map(config => getCardPreferenceOptionsForDfm({
    config,
    navigationLevel,
    campaignType,
    preference,
    permissions,
  })), [campaignType, navigationLevel, permissions, preference, selectedCardsConfigs]);

  const pageViewPortCardSizeFallback = getViewPortCardsCount(selectedCardsConfigs);
  const pageViewPortSizeFromDfm = window.FluentOverviewPageDataFetchManagerCardsConfigObject
    && window.FluentOverviewPageDataFetchManagerCardsConfigObject.viewPortCardSize;
  // we don't change this card size value after initializing it with useState
  const [pageViewPortCardSize, setPageViewPortCardSize] = useState(pageViewPortSizeFromDfm || pageViewPortCardSizeFallback);
  const [viewportReady, setViewportReady] = useState(false);

  const tiles = ['Device'];
  const showTooMuchDataError = useHasTooManyAccounts({
    navigationLevel,
    dateRange,
    getCardsConfig,
    permissions,
    tiles,
    overviewPageActivity,
  });

  useEffect(() => {
    // On single campaign overview page, we don't have view port size from dfm
    // We may need to wait for webcome card config to be ready before we can get pageViewPortCardSizeFallback
    if ((!pageViewPortCardSize || pageViewPortCardSize < 1) && pageViewPortCardSizeFallback > 0) {
      setPageViewPortCardSize(pageViewPortCardSizeFallback);
    }
  }, [pageViewPortCardSize, pageViewPortCardSizeFallback]);

  useEffect(() => {
    // In case we have issues rendering cards in viewport, we always start to render cards outside of view port after 7 seconds.
    // 7 seconds is now about the P95 value of viewport rendering time
    const timeoutId = setTimeout(() => {
      setViewportReady(true);
    }, 7000);
    return () => clearTimeout(timeoutId);
  }, [dfmActivity]);

  const perfMarkers = useMemo(() => getChildPerfMarkers({
    perfMarker,
    configs: selectedCardsConfigs,
    cardSize: (window.FluentOverviewPageDataFetchManagerCardsConfigObject
      && window.FluentOverviewPageDataFetchManagerCardsConfigObject.viewPortCardSize)
      || pageViewPortCardSize,
  }), [pageViewPortCardSize, perfMarker, selectedCardsConfigs]);

  const perfMarkersWithSummaryCards = useMemo(() => {
    if (isShowCampaignStatusSummaryCards) {
      return getChildPerfMarkers({
        perfMarker,
        configs: SummaryCardsConfigs,
        cardSize: SummaryCardsConfigs.length,
      });
    }
    return {};
  }, [isShowCampaignStatusSummaryCards, perfMarker]);

  const contentReady = useCallback((id) => {
    if (perfMarkersWithSummaryCards[id] && perfMarkersWithSummaryCards[id].done) {
      perfMarkersWithSummaryCards[id].done();
    }
  }, [perfMarkersWithSummaryCards]);

  const campaignStatusContentReady = useCallback(() => contentReady(campaignStateCard), [contentReady]);
  const budgetContentReady = useCallback(() => contentReady(budgetCard), [contentReady]);
  const bidStrategyContentReady = useCallback(() => contentReady(bidStrategyCard), [contentReady]);

  useEffect(() => {
    if (window.FluentOverviewPageDataFetchManagerCardsConfigObject) {
      const config = getCardsConfig();
      const {
        config: dfmConfig,
        viewPortCardSize,
        isKeywordInViewPort,
        isSearchTermInViewPort,
        innerHeight,
        innerWidth,
        campaignType: dfmCampaignType,
      } = window.FluentOverviewPageDataFetchManagerCardsConfigObject;

      if (!_.isEmpty(config) && campaignType === dfmCampaignType) {
        if (!_.isEqual(dfmConfig, config)) {
          const strConfig = JSON.stringify(config);
          const strDfmConfig = JSON.stringify(dfmConfig);
          dfmActivity.trace(`DFM viewPortCardSize: ${viewPortCardSize} - innerHeight: ${innerHeight} - innerWidth: ${innerWidth}`);
          dfmActivity.trace(`CampaignType: ${campaignType} - CardsConfig changed from ${strDfmConfig} to ${strConfig}`);
        } else {
          const strConfig = JSON.stringify(config);
          dfmActivity.trace(`DFM card configs is consistent in new overview page: CampaignType: ${campaignType}, ${strConfig}`);
          dfmActivity.trace(`isKeywordInViewPort: ${isKeywordInViewPort}`);
          dfmActivity.trace(`isSearchTermInViewPort: ${isSearchTermInViewPort}`);
          dfmActivity.trace(`viewPortCardSize: ${viewPortCardSize}`);
        }

        if (viewPortCardSize !== pageViewPortCardSize) {
          dfmActivity.trace(`DFM viewPortCardSize: ${viewPortCardSize} - pageViewPortCardSize: ${pageViewPortCardSize}`);
        }

        delete window.FluentOverviewPageDataFetchManagerCardsConfigObject;
      }
    }
  }, [campaignType, dfmActivity, getCardsConfig, pageViewPortCardSize]);

  useEffect(() => {
    perfMarker.doneCallback = () => {
      PageReady.pageIsReady();
      setViewportReady(true);
      dfmActivity.trace('Overview Viewport Page Ready');
    };

    if (_.size(cardSelectionCards) > 0 && pageViewPortCardSize === 0) {
      perfMarker.doneCallback();
    }
  }, [pageViewPortCardSize, perfMarker, dfmActivity, cardSelectionCards]);

  const cards = useMemo(() => _.chain(selectedCardsConfigs)
    .first(viewportReady ? selectedCardsConfigs.length : pageViewPortCardSize)
    .map(cardConfig => getCardViewConstructor({
      config: cardConfig,
      instrumentation,
      constants,
      campaignType,
    }))
    .value(), [selectedCardsConfigs, viewportReady, pageViewPortCardSize, campaignType]);

  const campaignStatus = _.get(currentCampaign, 'Status');

  useEffect(() => {
    const { AppCampaignNewCampaignGridColumns } = permissions.dynamic || {};
    if (AppCampaignNewCampaignGridColumns) {
      setRefreshKey(_.uniqueId());
    }
    // the dependency array specifies props that, when changed, should trigger the re-calcuation of internal preference state
    // if this is needed, add the prop to the dependency array
  }, [permissions, campaignType, campaignStatus]);

  const renderCards = useCallback(() => (
    <>
      {_.map(cards, ({
        CardView,
        CardHeaderContent,
        config,
        getCardHeaderContentProps = _.noop,
        getCardViewProps = _.noop,
      }) => {
        const cardPreferenceKey = getCardPreferenceKey({
          cardTypeId: config.typeId,
          navigationLevel,
          campaignType,
        });
        const cardPreference = getCardPreferenceWithDefault({
          config,
          navigationLevel,
          campaignType,
          preference,
          permissions,
        });

        const patchPreference = getPreferencePatcher({
          key: cardPreferenceKey,
          preference,
        });
        const staticDepsProps = legacyDeps[config.typeId] || {};

        const perfMarkerUtils = {
          willFetchData: () => {
            if (perfMarkers[config.typeId]) {
              perfMarkers[config.typeId].willFetchData();
            }
          },
          willRender: () => {
            if (perfMarkers[config.typeId]) {
              perfMarkers[config.typeId].willRender();
            }
          },
          done: () => {
            if (perfMarkers[config.typeId]) {
              perfMarkers[config.typeId].done();
            }
          },
        };

        const isDiagnositcCard = config.typeId === cardDiagnosticId;
        const dynamicHeight = (config.dynamicHeightForNarrow && responsiveSize !== 'xl' && responsiveSize !== 'xxl') || isDiagnositcCard;
        const isFullWidthCard = (isPMaxCampaignOverview || isMobileAppCampaign) && (config.typeId === cardPerformanceId || isDiagnositcCard);

        return (
          <OverviewCard
            key={config.typeId}
            name={i18n.getString(config.name)}
            className={classNames(`card-${config.typeId}`, isFullWidthCard ? styles.cardWidth100 : getCardSizeClass(config.size, responsiveSize, styles), dynamicHeight ? styles.dynamicHeight : styles.cardBasic)}
            i18n={i18n}
            currentSize={config.size}
            CardHeaderContent={CardHeaderContent}
            cardHeaderContentProps={getCardHeaderContentProps({
              cardPreference, patchPreference, permissions, i18n, dateRange, isSDK,
            })}
          >
            <CardView
              i18n={i18n}
              key={refreshKey}
              dateRange={stableDateRange}
              filter={stableFilter}
              serverContext={serverContext}
              constructOptions={config.getEntitySetOptions}
              permissions={permissions}
              perfMarkerUtils={perfMarkerUtils}
              cardTypeId={config.typeId}
              isSDK={isSDK}
              getCardsConfig={getCardsConfig}
              {...getCardViewProps({
                cardPreference, patchPreference, permissions, i18n, preferenceService, navigationLevel, campaignType,
              })}
              responsiveSize={responsiveSize}
              {...staticDepsProps}
              {...welcomeTipsConfig}
            />
          </OverviewCard>
        );
      })}
    </>
  ), [campaignType, cards, dateRange, getCardsConfig, i18n, isPMaxCampaignOverview, navigationLevel, perfMarkers, permissions, preference, preferenceService, refreshKey, responsiveSize, serverContext, stableDateRange, stableFilter, styles, welcomeTipsConfig, isSDK]);


  const TimeZone = _.get(serverContext, ['CurrentAccount', 'TimeZone'], 0);
  const cardSelectorKey = `${navigationLevel}-${campaignType}`;
  const showCardSelector = !isLoadingWelcomeCardConfig && !showWelcomeTips && !isLinkedInCampaignOverview && !isPMaxCampaignOverview && !isMobileAppCampaign;

  return (
    <div className={classNames(['ba-overview-page', styles.pageContainer])}>
      {showTooMuchDataError ? (<TooMuchAccountsError i18n={i18n} />) :
        showCardSelector && <CardSelector key={cardSelectorKey} cards={cardSelectionCards} onSave={onCardSelectionSaved} />}
      {isShowCampaignStatusSummaryCards &&
        <CampaignStatusSummaryCards
          campaignStatusContentReady={campaignStatusContentReady}
          budgetContentReady={budgetContentReady}
          bidStrategyContentReady={bidStrategyContentReady}
          responsiveSize={responsiveSize}
          isLinkedInCampaignOverview={isLinkedInCampaignOverview}
        />
      }
      {!showTooMuchDataError &&
        <div className={classNames('ba-overview-page-cards-container', styles.cardsContainer)}>
          {renderCards()}
        </div>}
      {!showTooMuchDataError && !showWelcomeTips && !isSDK &&
        <div className={classNames('disclaimer', styles.disclaimer)}>
          <DisclaimerBanner timezone={TimeZone} />
        </div>
      }
    </div>
  );
};

OverviewPagePhase2.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  currentCampaign: PropTypes.shape({
    CampaignType: PropTypes.string,
  }),
  perfMarker: PropTypes.shape({
    done: PropTypes.func.isRequired,
    doneCallback: PropTypes.func,
  }).isRequired,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({
      AppCampaignNewCampaignGridColumns: PropTypes.bool,
    }),
  }).isRequired,
  serverContext: PropTypes.shape({
    CurrentAccount: PropTypes.shape({
      TimeZone: PropTypes.number,
    }),
  }).isRequired,
  preferenceService: PropTypes.shape({}).isRequired,
  isSDK: PropTypes.bool,
};

OverviewPagePhase2.defaultProps = {
  currentCampaign: null,
  isSDK: false,
};

